import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  FaCalendar,
  FaUser,
  FaClock,
  FaArrowRight,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import {
  CAROUSEL_INTERVAL_MS,
  CAROUSEL_SWIPE_THRESHOLD,
  CAROUSEL_SLIDE_TRANSITION_S,
  CAROUSEL_FADE_TRANSITION_S
} from '../config/featured';
import './FeaturedCarousel.css';

// Slide in from the side it came from, out towards the opposite side
const slideVariants = {
  enter: (direction) => ({ opacity: 0, x: direction >= 0 ? 40 : -40 }),
  center: { opacity: 1, x: 0 },
  exit: (direction) => ({ opacity: 0, x: direction >= 0 ? -40 : 40 })
};

const staticVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 }
};

const FeaturedCarousel = ({ slides = [], isLoading = false }) => {
  const prefersReducedMotion = useReducedMotion();
  const [[index, direction], setSlide] = useState([0, 0]);
  const [isPaused, setIsPaused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);

  const slideCount = slides.length;
  const hasControls = slideCount > 1;
  // Guard against the slide list shrinking (e.g. a refetch) under the current index
  const safeIndex = slideCount ? Math.min(index, slideCount - 1) : 0;
  const activeSlide = slides[safeIndex];

  const goTo = useCallback((nextIndex, nextDirection) => {
    setSlide(([current]) => {
      const total = Math.max(slideCount, 1);
      const wrapped = ((nextIndex % total) + total) % total;
      return [wrapped, nextDirection ?? (wrapped > current ? 1 : -1)];
    });
  }, [slideCount]);

  const goNext = useCallback(() => goTo(safeIndex + 1, 1), [goTo, safeIndex]);
  const goPrev = useCallback(() => goTo(safeIndex - 1, -1), [goTo, safeIndex]);

  // Don't burn through slides while the tab is in the background
  useEffect(() => {
    const handleVisibility = () => setIsTabHidden(document.hidden);
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Auto-advance. Keyed on the index so a manual move restarts the countdown.
  useEffect(() => {
    if (!hasControls || isPaused || isTabHidden || prefersReducedMotion) return undefined;

    const timer = setTimeout(() => goNext(), CAROUSEL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [safeIndex, hasControls, isPaused, isTabHidden, prefersReducedMotion, goNext]);

  const handleDragEnd = (event, info) => {
    if (!hasControls) return;
    if (info.offset.x < -CAROUSEL_SWIPE_THRESHOLD) goNext();
    else if (info.offset.x > CAROUSEL_SWIPE_THRESHOLD) goPrev();
  };

  const variants = prefersReducedMotion ? staticVariants : slideVariants;
  const transition = {
    duration: prefersReducedMotion ? 0.2 : CAROUSEL_SLIDE_TRANSITION_S,
    ease: 'easeInOut'
  };

  return (
    <section
      className="hero-blog"
      aria-roledescription="carousel"
      aria-label="Featured posts"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      {/* Crossfading background image for the active slide */}
      <AnimatePresence initial={false}>
        {activeSlide && (
          <motion.div
            key={`bg-${activeSlide.id}`}
            className="carousel-bg"
            style={{ backgroundImage: `url(${activeSlide.image})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.2 : CAROUSEL_FADE_TRANSITION_S }}
          />
        )}
      </AnimatePresence>

      <div className="hero-overlay"></div>

      <div className="hero-blog-content" aria-live="polite">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading latest post...</p>
          </div>
        ) : activeSlide ? (
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={activeSlide.id}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              drag={hasControls ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              className="carousel-slide"
              aria-roledescription="slide"
              aria-label={`${safeIndex + 1} of ${slideCount}`}
            >
              {/* Category badges */}
              {activeSlide.categories && activeSlide.categories.length > 0 && (
                <div className="hero-categories">
                  {activeSlide.categories.slice(0, 2).map(category => (
                    <Link
                      key={category.id}
                      to={`/blog?category=${category.id}`}
                      className="hero-category-badge"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}

              {/* Post title as hero heading */}
              <h1
                className="hero-blog-title"
                dangerouslySetInnerHTML={{ __html: activeSlide.title }}
              />

              {/* Post excerpt as subheading */}
              <p className="hero-blog-excerpt">{activeSlide.excerpt}</p>

              {/* Post meta information */}
              <div className="hero-meta">
                <span className="hero-meta-item">
                  <FaCalendar />
                  {new Date(activeSlide.date).toLocaleDateString()}
                </span>
                <span className="hero-meta-item">
                  <FaUser />
                  {activeSlide.author}
                </span>
                <span className="hero-meta-item">
                  <FaClock />
                  {activeSlide.readTime}
                </span>
              </div>

              {/* CTA Button */}
              <Link to={`/blog/${activeSlide.slug}`} className="btn btn-hero">
                Read This Post
                <FaArrowRight className="btn-icon" />
              </Link>
            </motion.div>
          </AnimatePresence>
        ) : (
          <div>
            <h1>Welcome to My Blog</h1>
            <p>No posts available yet.</p>
          </div>
        )}
      </div>

      {/* Prev / next arrows */}
      {hasControls && !isLoading && (
        <>
          <button
            type="button"
            className="carousel-arrow prev"
            onClick={goPrev}
            aria-label="Previous featured post"
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            className="carousel-arrow next"
            onClick={goNext}
            aria-label="Next featured post"
          >
            <FaChevronRight />
          </button>

          {/* Slide dots */}
          <div className="carousel-dots">
            {slides.map((slide, dotIndex) => (
              <button
                key={slide.id}
                type="button"
                className={`carousel-dot ${dotIndex === safeIndex ? 'active' : ''}`}
                onClick={() => goTo(dotIndex, dotIndex > safeIndex ? 1 : -1)}
                aria-label={`Show featured post ${dotIndex + 1}`}
                aria-current={dotIndex === safeIndex}
              />
            ))}
          </div>
        </>
      )}

      {/* Scroll down chevron */}
      <motion.div
        className="hero-scroll-chevron"
        animate={prefersReducedMotion ? undefined : { y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <FaChevronDown />
      </motion.div>
    </section>
  );
};

export default FeaturedCarousel;
